import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { AlertCircle, Target, Sprout } from 'lucide-react';
import { useFarmingResultsSelection } from '../../context/FarmingResultsSelectionContext';
import { 
  fetchSeedsToKill, 
  SeedsToKillSummary, 
  ProcessedSeedData 
} from '../../services/seedsToKillService';

export const SeedsToPossiblyAdjustDashboard: React.FC = () => {
  const { selectedCompany, selectedProduct } = useFarmingResultsSelection();
  const [data, setData] = useState<SeedsToKillSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedCompany && selectedProduct) {
      fetchData();
    }
  }, [selectedCompany, selectedProduct]);

  const fetchData = async () => {
    if (!selectedCompany || !selectedProduct) return;

    const asin = selectedProduct.id || selectedProduct.ASIN;
    if (!asin) {
      setError('No ASIN found for selected product');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`Fetching Seeds to Possibly Adjust data for: ${selectedCompany}/${asin}`);
      const result = await fetchSeedsToKill(selectedCompany, asin);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch Seeds to Possibly Adjust data';
      console.error('Error fetching Seeds to Possibly Adjust:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedCompany || !selectedProduct) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <Target className="h-12 w-12 text-app-primary mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Select Company & ASIN
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Choose a company and ASIN from the sidebar to view Seeds to Possibly Adjust data.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-app-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading Seeds to Possibly Adjust...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Error Loading Data
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error}
          </p>
          <Button onClick={fetchData} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!data || data.seeds.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <Sprout className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No Seeds Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            No seeds to possibly adjust found for this ASIN.
          </p>
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number | null): string => {
    if (value === null) return '-';
    return `$${value.toFixed(2)}`;
  };

  const formatNumber = (value: number): string => {
    return value.toFixed(2);
  };

  const formatPercent = (value: number | null): string => {
    if (value === null) return '-';
    return `${value.toFixed(2)}%`;
  };

  // Widget-specific formatting (whole numbers, except CPC)
  const formatCurrencyWidget = (value: number | null): string => {
    if (value === null) return '-';
    return `$${Math.round(value)}`;
  };

  const formatNumberWidget = (value: number): string => {
    return Math.round(value).toString();
  };

  const formatPercentWidget = (value: number | null): string => {
    if (value === null) return '-';
    return `${Math.round(value)}%`;
  };

  // Calculate summary metrics
  const calculateSummaryMetrics = () => {
    const totalClicks = data.seeds.reduce((sum, seed) => sum + seed.clicks, 0);
    const totalSpend = data.seeds.reduce((sum, seed) => sum + seed.spend, 0);
    const totalSales = data.seeds.reduce((sum, seed) => sum + seed.sales, 0);
    const totalOrders = data.seeds.reduce((sum, seed) => sum + seed.orders, 0);
    
    const validCostPerCst = data.seeds.filter(s => s.costPerCst !== null);
    const avgCostPerCst = validCostPerCst.length > 0
      ? validCostPerCst.reduce((sum, seed) => sum + (seed.costPerCst || 0), 0) / validCostPerCst.length
      : 0;
    
    const validAmountOverBudget = data.seeds.filter(s => s.amountOverBudget !== null);
    const avgAmountOverBudget = validAmountOverBudget.length > 0
      ? validAmountOverBudget.reduce((sum, seed) => sum + (seed.amountOverBudget || 0), 0) / validAmountOverBudget.length
      : null;
    
    const validAmountOverBudgetPercent = data.seeds.filter(s => s.amountOverBudgetPercent !== null);
    const avgAmountOverBudgetPercent = validAmountOverBudgetPercent.length > 0
      ? validAmountOverBudgetPercent.reduce((sum, seed) => sum + (seed.amountOverBudgetPercent || 0), 0) / validAmountOverBudgetPercent.length
      : null;
    
    const validCpc = data.seeds.filter(s => s.cpc !== null);
    const avgCpc = validCpc.length > 0
      ? validCpc.reduce((sum, seed) => sum + (seed.cpc || 0), 0) / validCpc.length
      : 0;

    return {
      totalClicks,
      totalSpend,
      totalSales,
      totalOrders,
      avgCostPerCst,
      avgAmountOverBudget,
      avgAmountOverBudgetPercent,
      avgCpc
    };
  };

  const metrics = calculateSummaryMetrics();

  return (
    <div className="space-y-6">
      {/* Summary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Clicks</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumberWidget(metrics.totalClicks)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Spend</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrencyWidget(metrics.totalSpend)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Sales</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrencyWidget(metrics.totalSales)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Orders</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumberWidget(metrics.totalOrders)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">Avg Cost Per CST</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrencyWidget(metrics.avgCostPerCst)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">Avg Amount Over Budget $</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrencyWidget(metrics.avgAmountOverBudget)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">Avg Amount Over Budget %</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatPercentWidget(metrics.avgAmountOverBudgetPercent)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">Avg CPC</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(metrics.avgCpc)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Table Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sprout className="h-5 w-5 text-app-primary" />
            Seeds to Possibly Adjust
          </CardTitle>
          <CardDescription>
            Seeds that meet the criteria for {selectedCompany} - {selectedProduct.id} ({data.totalSeeds} total)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <div className="min-w-full">
              <div className="flex border-b dark:border-gray-700 py-3 px-2 font-semibold text-gray-900 dark:text-gray-100 text-sm">
                <div className="w-[15%] text-left pr-4">Seed</div>
                <div className="w-[10%] text-right pr-4">Clicks</div>
                <div className="w-[10%] text-right pr-4">Spend</div>
                <div className="w-[10%] text-right pr-4">Sales</div>
                <div className="w-[10%] text-right pr-4">Orders</div>
                <div className="w-[12%] text-right pr-4">Cost Per CST</div>
                <div className="w-[12%] text-right pr-4">Amount Over Budget $</div>
                <div className="w-[12%] text-right pr-4">Amount Over Budget %</div>
                <div className="w-[9%] text-right">CPC</div>
              </div>
              
              <div className="space-y-0">
                {data.seeds.map((seed, index) => (
                  <div key={`${seed.seed}-${index}`} className="flex border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 py-3 px-2 text-sm">
                    <div className="w-[15%] text-left text-gray-900 dark:text-gray-100 pr-4 truncate" title={seed.seed}>
                      {seed.seed}
                    </div>
                    <div className="w-[10%] text-right text-gray-900 dark:text-gray-100 pr-4">
                      {Math.round(seed.clicks)}
                    </div>
                    <div className="w-[10%] text-right text-gray-900 dark:text-gray-100 pr-4">
                      ${Math.round(seed.spend)}
                    </div>
                    <div className="w-[10%] text-right text-gray-900 dark:text-gray-100 pr-4">
                      ${Math.round(seed.sales)}
                    </div>
                    <div className="w-[10%] text-right text-gray-900 dark:text-gray-100 pr-4">
                      {Math.round(seed.orders)}
                    </div>
                    <div className="w-[12%] text-right text-gray-900 dark:text-gray-100 pr-4">
                      {formatCurrency(seed.costPerCst)}
                    </div>
                    <div className="w-[12%] text-right pr-4">
                      {seed.amountOverBudget !== null ? (
                        <span className={`font-medium ${
                          seed.amountOverBudget > 0
                            ? 'text-red-600 dark:text-red-400'
                            : seed.amountOverBudget < 0
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {formatCurrency(seed.amountOverBudget)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                    <div className="w-[12%] text-right pr-4">
                      {seed.amountOverBudgetPercent !== null ? (
                        <span className={`font-medium ${
                          seed.amountOverBudgetPercent > 0
                            ? 'text-red-600 dark:text-red-400'
                            : seed.amountOverBudgetPercent < 0
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {formatPercent(seed.amountOverBudgetPercent)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                    <div className="w-[9%] text-right text-gray-900 dark:text-gray-100">
                      {formatCurrency(seed.cpc)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SeedsToPossiblyAdjustDashboard;